import { useState, useEffect } from "react";
import {
  getProductDetails,
  updateProductInventory,
  getProductSupplierProducts,
  updateProduct,
  type ProductDetails,
  type ProductSupplierProduct,
} from "../../../api/catalog";
import {
  listSuppliers,
  createSupplierProduct,
  updateSupplierProduct,
  type Supplier,
} from "../../../api/suppliers";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";

type ProductInventoryTabProps = {
  productId: string;
  onNavigateToGeneral?: () => void;
};

type DetailsShape = {
  product?: { product_type?: string; track_inventory?: boolean; code?: string | null };
  code?: string | null;
  default_supplier?: { id: string; name: string } | null;
  preferred_supplier_product?: {
    supplier_sku?: string;
    supplier_product_name?: string;
    unit_cost?: number;
    currency?: string;
    lead_time_days?: number | null;
    min_order_qty?: number | null;
  } | null;
  inventory?: {
    stock_quantity?: number | null;
    reserved_quantity?: number | null;
    quantity_on_order?: number | null;
    stock_type?: string | null;
    low_stock_threshold?: number | null;
  } | null;
};

const PHYSICAL_PRODUCT_TYPES = ["produit", "consommable", "hardware", "package"];
const NON_SUPPLY_TYPES = ["service", "data_processing"];

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-sm font-semibold text-text border-b border-border pb-2 mb-3">
      {children}
    </h4>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-muted shrink-0">{label}</span>
      <span className="text-text text-right">{value ?? "—"}</span>
    </div>
  );
}

export function ProductInventoryTab({ productId, onNavigateToGeneral }: ProductInventoryTabProps) {
  const [details, setDetails] = useState<ProductDetails | null>(null);
  const [supplierProducts, setSupplierProducts] = useState<ProductSupplierProduct[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stockQuantity, setStockQuantity] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [unitCostEdit, setUnitCostEdit] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [newLinkSku, setNewLinkSku] = useState("");
  const [newLinkRef, setNewLinkRef] = useState("");
  const [newLinkCost, setNewLinkCost] = useState("");
  const [newLinkCurrency, setNewLinkCurrency] = useState("CAD");
  const [newLinkLeadDays, setNewLinkLeadDays] = useState("");
  const [newLinkMinQty, setNewLinkMinQty] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [mainSkuEdit, setMainSkuEdit] = useState("");
  const [mainRefEdit, setMainRefEdit] = useState("");
  const [mainLeadDaysEdit, setMainLeadDaysEdit] = useState("");
  const [mainMinQtyEdit, setMainMinQtyEdit] = useState("");
  const [savingMainSupplier, setSavingMainSupplier] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getProductDetails(productId),
      getProductSupplierProducts(productId),
    ])
      .then(([data, list]) => {
        if (cancelled) return;
        setDetails(data);
        setSupplierProducts(Array.isArray(list) ? list : []);
        const inv = (data as DetailsShape).inventory;
        setStockQuantity(inv?.stock_quantity != null ? String(inv.stock_quantity) : "");
        setLowStockThreshold(
          inv?.low_stock_threshold != null ? String(inv.low_stock_threshold) : ""
        );
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (!details) return;
    listSuppliers({ status: "active", page_size: 500 })
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, [details]);

  const product = details
    ? (details as DetailsShape).product ?? (details as DetailsShape)
    : null;
  const productType = (product as { product_type?: string } | null)?.product_type ?? "";
  const trackInventory =
    (product as { track_inventory?: boolean } | null)?.track_inventory ??
    (details as { track_inventory?: boolean } | null)?.track_inventory ??
    false;
  const detailsShape = details as DetailsShape | null;
  const defaultSupplier = detailsShape?.default_supplier ?? null;
  const preferredSupplierProduct = detailsShape?.preferred_supplier_product ?? null;
  const inventory = detailsShape?.inventory ?? null;

  const isPhysicalType = PHYSICAL_PRODUCT_TYPES.includes(productType);
  const isNonSupplyType = NON_SUPPLY_TYPES.includes(productType);
  const isBundle = productType === "bundle";
  const productCode =
    (product as { code?: string | null } | null)?.code ??
    (details as { code?: string | null } | null)?.code ??
    "";

  const mainSupplierProduct = supplierProducts.find(
    (sp) => sp.is_preferred || sp.supplier_id === defaultSupplier?.id
  ) ?? supplierProducts[0];
  const costFromSupplier =
    preferredSupplierProduct?.unit_cost ?? mainSupplierProduct?.unit_cost;
  const costStable =
    costFromSupplier != null && !Number.isNaN(Number(costFromSupplier))
      ? Number(costFromSupplier)
      : null;
  useEffect(() => {
    setUnitCostEdit(costStable != null ? String(costStable) : "");
  }, [costStable]);

  useEffect(() => {
    if (mainSupplierProduct) {
      setMainSkuEdit(mainSupplierProduct.supplier_sku ?? "");
      setMainRefEdit(mainSupplierProduct.supplier_product_name ?? "");
      setMainLeadDaysEdit(mainSupplierProduct.lead_time_days != null ? String(mainSupplierProduct.lead_time_days) : "");
      setMainMinQtyEdit(mainSupplierProduct.min_order_qty != null ? String(mainSupplierProduct.min_order_qty) : "");
    }
  }, [mainSupplierProduct?.id, mainSupplierProduct?.supplier_sku, mainSupplierProduct?.supplier_product_name, mainSupplierProduct?.lead_time_days, mainSupplierProduct?.min_order_qty]);

  useEffect(() => {
    if (!isBundle || !selectedSupplierId || newLinkSku.trim()) return;
    if (productCode.trim()) setNewLinkSku(productCode.trim());
  }, [isBundle, selectedSupplierId, productCode, newLinkSku]);

  const refreshData = () => {
    Promise.all([getProductDetails(productId), getProductSupplierProducts(productId)]).then(
      ([data, list]) => {
        setDetails(data);
        setSupplierProducts(Array.isArray(list) ? list : []);
      }
    );
  };

  const handleDefineSupplier = async () => {
    if (!selectedSupplierId) return;
    setSavingLink(true);
    setError(null);
    try {
      await updateProduct(productId, { default_supplier_id: selectedSupplierId });
      refreshData();
      setSelectedSupplierId("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingLink(false);
    }
  };

  const handleCreateLinkAndDefine = async () => {
    if (!selectedSupplierId || !newLinkSku.trim()) {
      setError("Indiquez le SKU fournisseur.");
      return;
    }
    const unitCost = parseFloat(newLinkCost);
    if (Number.isNaN(unitCost) || unitCost < 0) {
      setError("Coût unitaire invalide.");
      return;
    }
    setSavingLink(true);
    setError(null);
    try {
      await createSupplierProduct(selectedSupplierId, {
        product_id: productId,
        supplier_sku: newLinkSku.trim(),
        supplier_product_name: newLinkRef.trim() || null,
        unit_cost: unitCost,
        currency: newLinkCurrency,
        lead_time_days: newLinkLeadDays.trim() ? parseInt(newLinkLeadDays, 10) || null : null,
        min_order_qty: newLinkMinQty.trim() ? parseInt(newLinkMinQty, 10) || null : null,
        is_preferred: true,
        is_active: true,
      });
      await updateProduct(productId, { default_supplier_id: selectedSupplierId });
      refreshData();
      setSelectedSupplierId("");
      setNewLinkSku("");
      setNewLinkRef("");
      setNewLinkCost("");
      setNewLinkLeadDays("");
      setNewLinkMinQty("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingLink(false);
    }
  };

  const handleSaveMainSupplier = async () => {
    if (!mainSupplierProduct?.supplier_id || !mainSupplierProduct?.id) return;
    const unitCost = parseFloat(unitCostEdit);
    if (Number.isNaN(unitCost) || unitCost < 0) {
      setError("Coût unitaire invalide.");
      return;
    }
    setSavingMainSupplier(true);
    setError(null);
    try {
      await updateSupplierProduct(mainSupplierProduct.supplier_id, mainSupplierProduct.id, {
        supplier_sku: mainSkuEdit.trim() || mainSupplierProduct.supplier_sku,
        supplier_product_name: mainRefEdit.trim() || null,
        unit_cost: unitCost,
        lead_time_days: mainLeadDaysEdit.trim() ? parseInt(mainLeadDaysEdit, 10) || null : null,
        min_order_qty: mainMinQtyEdit.trim() ? parseInt(mainMinQtyEdit, 10) || null : null,
      });
      refreshData();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingMainSupplier(false);
    }
  };

  const handleChangeDefaultSupplier = async (supplierId: string) => {
    if (!supplierId) return;
    setSavingLink(true);
    setError(null);
    try {
      await updateProduct(productId, { default_supplier_id: supplierId });
      refreshData();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingLink(false);
    }
  };

  const handleSaveStock = async () => {
    if (!trackInventory) return;
    setSaving(true);
    setError(null);
    try {
      await updateProductInventory(productId, {
        stock_quantity: parseFloat(stockQuantity) || 0,
      });
      const data = await getProductDetails(productId);
      setDetails(data);
      const inv = (data as DetailsShape).inventory;
      setStockQuantity(inv?.stock_quantity != null ? String(inv.stock_quantity) : "");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReplenishment = async () => {
    if (!trackInventory) return;
    setSaving(true);
    setError(null);
    try {
      await updateProductInventory(productId, {
        low_stock_threshold:
          lowStockThreshold.trim() === "" ? null : parseFloat(lowStockThreshold) || null,
      });
      const data = await getProductDetails(productId);
      setDetails(data);
      const inv = (data as DetailsShape).inventory;
      setLowStockThreshold(
        inv?.low_stock_threshold != null ? String(inv.low_stock_threshold) : ""
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted">Chargement…</div>;
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-sm text-danger">{error}</p>
      </div>
    );
  }

  if (isNonSupplyType) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-sm text-muted">
          L’onglet Approvisionnement s’applique aux produits physiques et consommables. Pour un
          service ou un traitement de données, il n’y a pas de fournisseur ni de stock à gérer.
        </p>
      </div>
    );
  }

  if (!trackInventory && !isBundle) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-sm text-muted">
          Ce produit n’est pas en suivi d’inventaire. Activez le suivi dans l’onglet Général pour
          gérer le stock et les fournisseurs.
        </p>
        {onNavigateToGeneral && (
          <Button label="Aller à l'onglet Général" intent="secondary" onClick={onNavigateToGeneral} />
        )}
      </div>
    );
  }

  const stockQty = inventory?.stock_quantity ?? 0;
  const threshold = inventory?.low_stock_threshold ?? null;
  const stockStatus =
    threshold != null
      ? stockQty <= 0
        ? "Rupture"
        : stockQty <= threshold
          ? "Faible"
          : "Normal"
      : "Normal";

  const alternateSuppliers = supplierProducts.filter(
    (sp) => sp.id !== mainSupplierProduct?.id
  );
  const unitCost = preferredSupplierProduct?.unit_cost ?? mainSupplierProduct?.unit_cost ?? 0;
  const currency = preferredSupplierProduct?.currency ?? mainSupplierProduct?.currency ?? "CAD";

  return (
    <div className="p-6 space-y-8">
      {error && (
        <div className="rounded-md bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      {/* 1. Fournisseur principal */}
      <section>
        <SectionTitle>Fournisseur principal</SectionTitle>
        <p className="text-xs text-muted mb-3">
          {isBundle
            ? "Liez ce bundle à un fournisseur pour le commander dans Inventaire → Commandes (SKU, coût d’achat du kit complet)."
            : "D’où provient le produit : fournisseur, référence et coût unitaire."}
        </p>
        {defaultSupplier || mainSupplierProduct ? (
          <div className="rounded-md border border-border bg-muted/10 p-4 space-y-4">
            {supplierProducts.length > 1 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted shrink-0">Fournisseur principal</span>
                <select
                  value={defaultSupplier?.id ?? mainSupplierProduct?.supplier_id ?? ""}
                  onChange={(e) => handleChangeDefaultSupplier(e.target.value)}
                  disabled={savingLink}
                  className="h-10 min-w-[200px] rounded-md border border-border bg-surface px-3 text-sm text-text"
                >
                  {supplierProducts.map((sp) => (
                    <option key={sp.id} value={sp.supplier_id}>
                      {sp.supplier_name ?? sp.supplier_id}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <FieldRow
                label="Fournisseur"
                value={defaultSupplier?.name ?? mainSupplierProduct?.supplier_name ?? "—"}
              />
            )}
            {mainSupplierProduct && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="SKU fournisseur"
                    value={mainSkuEdit}
                    onChange={(e) => setMainSkuEdit(e.target.value)}
                    placeholder="Ex. BC.AG.SS001075"
                  />
                  <Input
                    label="Référence fabricant"
                    value={mainRefEdit}
                    onChange={(e) => setMainRefEdit(e.target.value)}
                    placeholder="Optionnel"
                  />
                </div>
                <div className="flex flex-wrap items-end gap-3 py-1">
                  <span className="text-sm text-muted shrink-0 w-full sm:w-auto">Coût unitaire</span>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={unitCostEdit}
                      onChange={(e) => setUnitCostEdit(e.target.value)}
                      aria-label="Coût unitaire chez le fournisseur"
                      className="h-10 w-[140px] rounded-md border border-border bg-surface px-3 text-sm text-text"
                    />
                    <span className="text-sm text-muted shrink-0">{currency}</span>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="Délai d’approvisionnement (jours)"
                    type="number"
                    min={0}
                    value={mainLeadDaysEdit}
                    onChange={(e) => setMainLeadDaysEdit(e.target.value)}
                    placeholder="Optionnel"
                  />
                  <Input
                    label="Quantité minimum de commande"
                    type="number"
                    min={0}
                    value={mainMinQtyEdit}
                    onChange={(e) => setMainMinQtyEdit(e.target.value)}
                    placeholder="Optionnel"
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    label="Enregistrer les informations fournisseur"
                    onClick={handleSaveMainSupplier}
                    disabled={savingMainSupplier}
                    variant="primary"
                  />
                </div>
              </>
            )}
            {!mainSupplierProduct && (
              <>
                <FieldRow label="SKU fournisseur" value={preferredSupplierProduct?.supplier_sku ?? "—"} />
                <FieldRow
                  label="Référence fabricant"
                  value={preferredSupplierProduct?.supplier_product_name ?? "—"}
                />
                <FieldRow
                  label="Coût unitaire"
                  value={unitCost != null ? `${Number(unitCost).toFixed(2)} ${currency}` : "—"}
                />
                <FieldRow label="Devise" value={currency} />
                <FieldRow label="Délai (jours)" value={preferredSupplierProduct?.lead_time_days ?? "—"} />
              </>
            )}
          </div>
        ) : (
          <div className="rounded-md border border-border bg-muted/10 p-4 space-y-4">
            <p className="text-sm text-muted">
              Aucun fournisseur principal. Choisissez un fournisseur ci-dessous pour l’associer à ce produit.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="supplier-select" className="text-sm text-muted shrink-0">
                Fournisseur
              </label>
              <select
                id="supplier-select"
                value={selectedSupplierId}
                onChange={(e) => setSelectedSupplierId(e.target.value)}
                className="h-10 min-w-[220px] rounded-md border border-border bg-surface px-3 text-sm text-text"
              >
                <option value="">— Sélectionner —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedSupplierId && (
              <>
                {supplierProducts.some((sp) => sp.supplier_id === selectedSupplierId) ? (
                  <Button
                    label="Définir comme fournisseur principal"
                    onClick={handleDefineSupplier}
                    disabled={savingLink}
                    variant="primary"
                  />
                ) : (
                  <div className="space-y-3 pt-2 border-t border-border">
                    <p className="text-sm text-muted">
                      Ce fournisseur n’est pas encore lié à ce produit. Renseignez les informations puis créez le lien.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input
                        label="SKU fournisseur"
                        value={newLinkSku}
                        onChange={(e) => setNewLinkSku(e.target.value)}
                        placeholder="Obligatoire"
                      />
                      <Input
                        label="Référence fabricant"
                        value={newLinkRef}
                        onChange={(e) => setNewLinkRef(e.target.value)}
                        placeholder="Optionnel"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Input
                        label="Coût unitaire"
                        type="number"
                        min={0}
                        step="0.01"
                        value={newLinkCost}
                        onChange={(e) => setNewLinkCost(e.target.value)}
                        placeholder="0"
                        className="w-[120px]"
                      />
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-muted">Devise</label>
                        <select
                          value={newLinkCurrency}
                          onChange={(e) => setNewLinkCurrency(e.target.value)}
                          className="h-10 rounded-md border border-border bg-surface px-2 text-sm"
                        >
                          <option value="CAD">CAD</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input
                        label="Délai (jours)"
                        type="number"
                        min={0}
                        value={newLinkLeadDays}
                        onChange={(e) => setNewLinkLeadDays(e.target.value)}
                        placeholder="Optionnel"
                      />
                      <Input
                        label="Quantité min. commande"
                        type="number"
                        min={0}
                        value={newLinkMinQty}
                        onChange={(e) => setNewLinkMinQty(e.target.value)}
                        placeholder="Optionnel"
                      />
                    </div>
                    <Button
                      label="Créer le lien et définir comme fournisseur principal"
                      onClick={handleCreateLinkAndDefine}
                      disabled={savingLink || !newLinkSku.trim()}
                      variant="primary"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {alternateSuppliers.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-text mb-2">Fournisseurs alternatifs</p>
            <ul className="space-y-2 text-sm">
              {alternateSuppliers.map((sp) => (
                <li
                  key={sp.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded border border-border bg-surface px-3 py-2"
                >
                  <span className="font-medium text-text">
                    {sp.supplier_name ?? sp.supplier_id}
                  </span>
                  <span className="text-muted">SKU : {sp.supplier_sku}</span>
                  <span className="text-text">
                    {sp.unit_cost != null ? `${Number(sp.unit_cost).toFixed(2)} ${sp.currency ?? "CAD"}` : "—"}
                  </span>
                  {sp.lead_time_days != null && (
                    <span className="text-muted">Délai : {sp.lead_time_days} j</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted mt-2">
              Pour ajouter un fournisseur alternatif, utilisez la fiche fournisseur ou le module
              Fournisseurs.
            </p>
          </div>
        )}
      </section>

      {/* 2. Coût d'achat */}
      <section>
        <SectionTitle>Coût d’achat</SectionTitle>
        <p className="text-xs text-muted mb-3">
          Coût d’acquisition du produit (alimente les calculs de marge dans Coûts internes).
        </p>
        <div className="rounded-md border border-border bg-muted/10 p-4 space-y-2">
          <FieldRow
            label="Coût fournisseur (unitaire)"
            value={unitCost != null ? `${Number(unitCost).toFixed(2)} ${currency}` : "—"}
          />
          <FieldRow label="Frais de transport estimés" value="—" />
          <FieldRow label="Frais d’importation / manutention" value="—" />
          <FieldRow
            label="Coût d’acquisition total (unitaire)"
            value={unitCost != null ? `${Number(unitCost).toFixed(2)} ${currency}` : "—"}
          />
        </div>
      </section>

      {/* 3–4. Stock et réapprovisionnement (masqués pour les bundles) */}
      {!isBundle && (
      <>
      <section>
        <SectionTitle>Gestion du stock</SectionTitle>
        <p className="text-xs text-muted mb-3">
          Quantité en stock, type de gestion et état (normal, faible, rupture).
        </p>
        <div className="rounded-md border border-border bg-muted/10 p-4 space-y-4">
          <FieldRow label="Type de gestion" value="Suivi simple" />
          <FieldRow
            label="Quantité en stock actuelle"
            value={inventory?.stock_quantity != null ? String(inventory.stock_quantity) : "0"}
          />
          <FieldRow
            label="Stock réservé / engagé"
            value={
              (inventory?.reserved_quantity ?? 0) > 0
                ? String(inventory.reserved_quantity)
                : "—"
            }
          />
          <FieldRow label="État du stock" value={stockStatus} />
          <div className="flex flex-wrap items-end gap-3 pt-2">
            <Input
              label="Ajuster la quantité en stock"
              type="number"
              min={0}
              value={stockQuantity}
              onChange={(e) => setStockQuantity(e.target.value)}
            />
            <Button label="Enregistrer" onClick={handleSaveStock} disabled={saving} />
          </div>
        </div>
      </section>

      {/* 4. Réapprovisionnement */}
      <section>
        <SectionTitle>Réapprovisionnement</SectionTitle>
        <p className="text-xs text-muted mb-3">
          Seuils et paramètres pour anticiper les achats (quand commander).
        </p>
        <div className="rounded-md border border-border bg-muted/10 p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label="Seuil de réapprovisionnement (alerte si stock ≤)"
              type="number"
              min={0}
              value={lowStockThreshold}
              onChange={(e) => setLowStockThreshold(e.target.value)}
              placeholder="Optionnel"
            />
            <Button label="Enregistrer" onClick={handleSaveReplenishment} disabled={saving} />
          </div>
          <FieldRow
            label="Quantité minimale de commande"
            value={
              preferredSupplierProduct?.min_order_qty != null
                ? String(preferredSupplierProduct.min_order_qty)
                : mainSupplierProduct?.min_order_qty != null
                  ? String(mainSupplierProduct.min_order_qty)
                  : "—"
            }
          />
          <FieldRow
            label="Délai fournisseur (jours)"
            value={
              preferredSupplierProduct?.lead_time_days ??
              mainSupplierProduct?.lead_time_days ??
              "—"
            }
          />
          <FieldRow label="Stratégie" value="Manuel" />
        </div>
      </section>
      </>
      )}
    </div>
  );
}
